import {
  Box,
  Button,
  ChakraProvider,
  extendTheme,
  Flex,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { bytesToMb } from '@lib/util/units';
import { UpdateSnapSiteConfigRequestBody } from '@snap-types/actions/update-snap-site-config';
import { FeatureVisibility, WonLossStatus } from '@snap-types/models/enums';
import {
  SnapSiteConfigEditFormData,
  snapSiteConfigEditSchema,
} from '@snap-types/models/snap-site-config';
import { objectKeysTyped } from '@snap-types/util/object-iterators';
import { WarningIcon } from '@ui/components/Icons';
import { PageHeader } from '@ui/components/layout/PageHeader';
import { PageWrapper } from '@ui/components/layout/PageWrapper';
import { QueryError, QueryLoading } from '@ui/components/query';
import { Show } from '@ui/components/Show';
import { Router } from '@ui/config/routes';
import { theme } from '@ui/config/theme';
import { useUpdateSnapSiteConfigMutation } from '@ui/endpoints/update-snap-site-config';
import { useViewSnapSiteConfigQuery } from '@ui/endpoints/view-snap-site-config';
import { isAutoConfiguratorSite } from '@ui/features/alpha/constants';
import {
  PART_SEARCH_COLUMNS_CUSTOM_NAMES,
  PART_SEARCH_COLUMNS_FIXED_NAMES,
} from '@ui/features/quote-builder/components/PartDetailedSearch/PartDetailedSearchColumnChooserMenu';
import { SnapSiteConfigEditContext } from '@ui/features/snap-site-config/screens/edit/context';
import { useCurrentSite, useCurrentSiteCode } from '@ui/hooks/useCurrentSite';
import {
  type TabDefinition,
  useTabbedFormErrors,
} from '@ui/hooks/useTabbedFormErrors';
import { useValidationToast } from '@ui/hooks/useToast';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';

import { BasicConfigPanel } from './BasicConfigPanel';
import { AutoConfiguratorImportPanel } from './components/AutoConfiguratorImportPanel';
import { PriceStrategyConfigsForm } from './components/PriceStrategyConfigsForm';
import { PartInsightsPanel } from './PartInsightsPanel';
import { PartSearchPanel } from './PartSearchPanel';
import { TeamsPanel } from './TeamsPanel';

const customTheme = extendTheme(theme, {
  components: {
    FormLabel: {
      baseStyle: {
        fontSize: 'sm',
      },
    },
  },
});

export function SnapSiteConfigEdit() {
  const siteCode = useCurrentSiteCode();
  const { snapSiteConfigUuid } = useParams() as { snapSiteConfigUuid: string };
  const viewSnapSiteConfigQuery = useViewSnapSiteConfigQuery({
    siteCode,
    snapSiteConfigUuid,
  });

  if (viewSnapSiteConfigQuery.isLoading) {
    return <QueryLoading title="Loading Snap Site Config..." />;
  }

  if (viewSnapSiteConfigQuery.isError) {
    return <QueryError error={viewSnapSiteConfigQuery.error} />;
  }

  if (viewSnapSiteConfigQuery.isSuccess) {
    return (
      <SnapSiteConfigEditUI
        defaultValues={{
          ...viewSnapSiteConfigQuery.data.snapSiteConfig,
        }}
        marketCodes={viewSnapSiteConfigQuery.data.marketCodes}
        discountCodes={viewSnapSiteConfigQuery.data.discountCodes}
      />
    );
  }

  return null;
}

const FormErrorsTabIcon = () => <WarningIcon color="snap.red.700" pl={2} />;

const BASE_ADMIN_TABS = [
  {
    key: 'teams',
    label: 'Teams',
    Component: TeamsPanel,
    fields: ['site.teams'],
  },
  {
    key: 'basic',
    label: 'Basic Configuration',
    Component: BasicConfigPanel,
    fields: [
      'erpDB',
      'useConfigurator',
      'useImportLineItems',
      'consolidateImportedLineItems',
      'useQuoteWonLossStatus',
      'decimalPlaces',
      'daysUntilExpiration',
      'daysUntilExpectedWin',
      // PartSpecTable temporarily disabled, see note in component below
      // 'partSpecTable',
      'defaultWonLossStatus',
      'sellingUnitOfMeasure',
      'leadTimeFormat',
      'productionDaysPerWeek',
      'transitDaysPerWeek',
      'planningDaysPerWeek',
      'showCostFieldsForFixedPriceStrategies',
      'defaultPartRequirementFixedCost',
      'duplicateQuoteVisible',
      'searchExistingCustomerPartIdsVisible',
      'alphaVisible',
      'sendImportFilesToVisual',
      'importFileSizeLimitBytes',
    ],
  },
  {
    key: 'price-strategies',
    label: 'Price Strategies',
    Component: PriceStrategyConfigsForm,
    fields: ['priceStrategies'],
  },
  {
    key: 'part-insights',
    label: 'Part Insights',
    Component: PartInsightsPanel,
    fields: [
      'partInsightsCrossSitePartIdVisible',
      'partInsightsPartHistoryVisible',
      'partInsightsLeadTimeVisible',
      'partInsightsMaxDaysForRecent',
      'partInsightsMaxDaysForInclusion',
      'partInsightsLowWinRate',
      'partInsightsHighWinRate',
      'partInsightsLastOrderGrossMarginThreshold',
      'partInsightsLastOrderGrossMarginVisible',
      'partInsightsLeadTimeRangeLow',
      'partInsightsLeadTimeRangeHigh',
      'partInsightsLeadTimeMaxDaysForInclusion',
    ],
  },
  {
    key: 'part-search',
    label: 'Part Search Fields',
    Component: PartSearchPanel,
    fields: [
      'partSearchVisible',
      ...PART_SEARCH_COLUMNS_CUSTOM_NAMES.map(
        (name) => `partSearchField_${name}_enabled`,
      ),
      ...PART_SEARCH_COLUMNS_CUSTOM_NAMES.map(
        (name) => `partSearchField_${name}_name`,
      ),
      ...objectKeysTyped(PART_SEARCH_COLUMNS_FIXED_NAMES).map(
        (name) => `partSearchField_${name}_enabled`,
      ),
    ],
  },
] as const satisfies TabDefinition[];

const AUTO_CONFIGURATOR_TAB_KEY = 'auto-configurator' as const;

type BaseAdminTabKeys = (typeof BASE_ADMIN_TABS)[number]['key'];
type AdminTabKeys = BaseAdminTabKeys | typeof AUTO_CONFIGURATOR_TAB_KEY;

function SnapSiteConfigEditUI({
  defaultValues,
  marketCodes,
  discountCodes,
}: {
  defaultValues?: Partial<UpdateSnapSiteConfigRequestBody['snapSiteConfig']>;
  marketCodes: string[];
  discountCodes: string[];
}) {
  const navigate = useNavigate();
  const currentSite = useCurrentSite();
  const { genericErrorToast, genericSuccessToast } = useValidationToast();
  const { snapSiteConfigUuid } = useParams() as { snapSiteConfigUuid: string };

  const form = useForm({
    resolver: zodResolver(snapSiteConfigEditSchema),
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      decimalPlaces: 2,
      defaultWonLossStatus: WonLossStatus.QUOTED,
      productionDaysPerWeek: 7,
      transitDaysPerWeek: 7,
      planningDaysPerWeek: 7,
      priceStrategies: [],
      ...defaultValues,
      // Convert bytes to integer MB for form display
      importFileSizeLimitBytes: defaultValues?.importFileSizeLimitBytes
        ? Math.round(bytesToMb(defaultValues.importFileSizeLimitBytes))
        : undefined,
    },
  });

  const [currentTab, setCurrentTab] = useState<AdminTabKeys>('basic');
  const autoConfiguratorVisibilityValue = form.watch('alphaVisible') as
    | FeatureVisibility
    | undefined;
  const autoConfiguratorVisibility =
    autoConfiguratorVisibilityValue ?? FeatureVisibility.NONE;
  const isAutoConfiguratorEnabled =
    autoConfiguratorVisibility !== FeatureVisibility.NONE;
  const isAutoConfiguratorTabVisible =
    isAutoConfiguratorSite(currentSite.code) && isAutoConfiguratorEnabled;
  const isAutoConfiguratorTabActive = currentTab === AUTO_CONFIGURATOR_TAB_KEY;

  const adminTabs = useMemo<readonly TabDefinition[]>(() => {
    if (!isAutoConfiguratorTabVisible) {
      return BASE_ADMIN_TABS;
    }

    return [
      ...BASE_ADMIN_TABS,
      {
        key: AUTO_CONFIGURATOR_TAB_KEY,
        label: 'Auto Configurator',
        Component: () => (
          <AutoConfiguratorImportPanel
            visibility={autoConfiguratorVisibility}
          />
        ),
        fields: [],
      },
    ];
  }, [autoConfiguratorVisibility, isAutoConfiguratorTabVisible]);

  useEffect(() => {
    if (!adminTabs.some((tab) => tab.key === currentTab)) {
      setCurrentTab(adminTabs[0]?.key as AdminTabKeys);
    }
  }, [adminTabs, currentTab]);

  const formErrorsByTab = useTabbedFormErrors(
    form,
    adminTabs,
    currentTab,
    setCurrentTab,
  );

  const [updateSnapSiteConfig, updateSnapSiteConfigResult] =
    useUpdateSnapSiteConfigMutation();

  async function handleSubmit(data: SnapSiteConfigEditFormData) {
    try {
      await updateSnapSiteConfig({
        params: {
          siteCode: currentSite.code,
          snapSiteConfigUuid,
        },
        body: {
          snapSiteConfig: data,
        },
      }).unwrap();

      genericSuccessToast(
        'Site config updated successfully.',
        'Your changes have been saved.',
      );
    } catch (error) {
      console.error('Failed to update site config:', error);
      genericErrorToast('Failed to update site config.', 'Please try again.');
    }
  }

  const contextValue = useMemo(
    () => ({
      marketCodes,
      discountCodes,
    }),
    [marketCodes, discountCodes],
  );

  return (
    <SnapSiteConfigEditContext.Provider value={contextValue}>
      <FormProvider {...form}>
        <PageWrapper>
          <PageHeader
            title={`Edit ${currentSite.name} (${currentSite.code}) Site Config`}
            subtitle={
              <Link
                as={RouterLink}
                to={Router.sitePath(currentSite.code, Router.routes.adminSites)}
              >
                ← Back to Sites
              </Link>
            }
          />

          <PageLayout>
            <ChakraProvider theme={customTheme}>
              <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
                <VStack
                  as="fieldset"
                  gap={6}
                  disabled={form.formState.isSubmitting}
                >
                  <Tabs
                    width="100%"
                    isLazy
                    lazyBehavior="keepMounted"
                    index={Math.max(
                      adminTabs.findIndex((tab) => tab.key === currentTab),
                      0,
                    )}
                    onChange={(index) => {
                      setCurrentTab(adminTabs[index].key as AdminTabKeys);
                    }}
                  >
                    <TabList>
                      {adminTabs.map((tab) => (
                        <Tab
                          key={tab.key}
                          onClick={(e) => e.preventDefault()}
                          onMouseUp={(e) => e.preventDefault()}
                          onPointerUp={(e) => e.preventDefault()}
                        >
                          {tab.label}
                          <Show when={formErrorsByTab[tab.key].length > 0}>
                            <FormErrorsTabIcon />
                          </Show>
                        </Tab>
                      ))}
                    </TabList>

                    <TabPanels>
                      {adminTabs.map(({ key, Component }) => (
                        <TabPanel key={key}>
                          <Component />
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>

                  {isAutoConfiguratorTabActive ? null : (
                    <Flex
                      w="full"
                      as="footer"
                      justifyContent="flex-end"
                      gap={4}
                      px={4}
                    >
                      <Button
                        type="button"
                        bg="snap.yellow.600"
                        rounded="sm"
                        _hover={{
                          backgroundColor: 'snap.yellow.700',
                        }}
                        onClick={() => {
                          // @TODO(shawk): confirm before navigate (losing form state)
                          form.reset();
                          void navigate(
                            Router.sitePath(
                              currentSite.code,
                              Router.routes.adminSites,
                            ),
                          );
                        }}
                        isDisabled={updateSnapSiteConfigResult.isLoading}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        colorScheme="teal"
                        rounded="sm"
                        isLoading={updateSnapSiteConfigResult.isLoading}
                        loadingText="Saving..."
                      >
                        Save Changes
                      </Button>
                    </Flex>
                  )}
                </VStack>
              </form>
            </ChakraProvider>
          </PageLayout>
        </PageWrapper>
      </FormProvider>
    </SnapSiteConfigEditContext.Provider>
  );
}

function PageLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box as="main" pb={4}>
      {children}
    </Box>
  );
}
